import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Search, Loader2 } from "lucide-react";
import { useDebounce } from "@/hooks/use-debounce";
import { useGlobalSearch } from "@/hooks/use-global-search";

interface SearchResult {
  id: string;
  type: 'animal' | 'facility' | 'permit' | 'enclosure';
  title: string;
  subtitle?: string;
  extra?: string;
  path: string;
}

export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const debouncedSearch = useDebounce(search, 250);

  const { data, isLoading } = useGlobalSearch({
    query: debouncedSearch,
    limit: 5,
  });

  const results: SearchResult[] = (data?.items || []).map((item) => {
    let path = '';
    if (item.type === 'animal') path = `/admin/animals/${item.id}`;
    else if (item.type === 'facility') path = `/admin/facilities/${item.id}`;
    else if (item.type === 'permit') path = `/admin/permits`;
    else if (item.type === 'enclosure') path = `/admin/facilities`;

    return {
      id: item.id,
      type: item.type,
      title: item.title,
      subtitle: item.subtitle,
      extra: item.extra,
      path
    };
  });

  const handleSelect = useCallback((result: SearchResult) => {
    navigate(result.path);
    setOpen(false);
    setSearch("");
  }, [navigate]);

  const getTypeIcon = (type: SearchResult['type']) => {
    const icons = {
      animal: '🐾',
      facility: '🏢',
      permit: '📄',
      enclosure: '🗺️',
    };
    return icons[type];
  };

  // Keyboard shortcut: / to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === '/' && !open && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
        setSearch("");
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="w-full sm:w-64 justify-start gap-2" aria-label="Global search">
          <Search className="h-4 w-4 shrink-0" />
          <span className="hidden sm:inline truncate text-muted-foreground">Search animals, facilities...</span>
          <span className="sm:hidden text-muted-foreground">Search</span>
          <kbd className="ml-auto hidden sm:inline-flex h-5 shrink-0 select-none items-center rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
            /
          </kbd>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Search animals, facilities, permits, enclosures..." 
            value={search}
            onValueChange={setSearch}
            aria-label="Search input"
          />
          <CommandList>
            {isLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
            {!isLoading && search.length >= 2 && results.length === 0 && (
              <CommandEmpty>No results found.</CommandEmpty>
            )}
            {!isLoading && results.length > 0 && (
              <>
                <CommandGroup heading="Results">
                  {results.map((result) => (
                    <CommandItem
                      key={`${result.type}-${result.id}`}
                      onSelect={() => handleSelect(result)}
                      className="cursor-pointer"
                    >
                      <span className="mr-2">{getTypeIcon(result.type)}</span>
                      <div className="flex flex-col flex-1">
                        <span className="font-medium">{result.title}</span>
                        {result.subtitle && (
                          <span className="text-xs text-muted-foreground">{result.subtitle}</span>
                        )}
                        {result.extra && (
                          <span className="text-xs text-muted-foreground">{result.extra}</span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
                {search && (
                  <div className="border-t p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start text-sm"
                      onClick={() => {
                        navigate(`/admin/search?q=${encodeURIComponent(search)}`);
                        setOpen(false);
                      }}
                    >
                      See all results for "{search}"
                    </Button>
                  </div>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
